import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { lastValueFrom, skip, take, takeUntil, tap } from 'rxjs';

import {
  CustomFormDetailsFragment, CustomFormFieldDetailsFragment, CustomFormFieldObjectTypeTypes,
  GetEntityById2GQL, GetEntityById2Query, NoteTarget
} from 'generated/graphql';

import { GetCustomFormService } from 'app/custom-forms/GetCustomForm.service';
import { NoteDialogComponent } from 'app/note/dialogs/note-dialog/note-dialog.component';
import { ToastrService } from 'core/toastr.service';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { cloneDeep } from 'lodash-es';

@Component({
  selector: 'app-contact-attempt-data-entry',
  templateUrl: './contact-attempt-data-entry.component.html',
  styleUrls: ['./contact-attempt-data-entry.component.scss']
})

export class ContactAttemptDataEntryComponent extends WithDisposableSubject()
  implements OnInit {
  @Input() entityId: string;
  @Input() campaignId: string;

  loading = false;
  customForm: CustomFormDetailsFragment = null;
  customFormComponents: Array<{ component: string; formField: CustomFormFieldDetailsFragment }> = [];
  entity: GetEntityById2Query['getEntity'] = null;

  /**
   *
   */
  constructor(
    private _getEntityByIdGQL: GetEntityById2GQL,
    private _getCustomFormSvc: GetCustomFormService,
    private _toastrService: ToastrService,
    private _cd: ChangeDetectorRef,
    private _dialog: MatDialog
  ) {
    super();
  }

  async ngOnInit() {
    const { data } = await lastValueFrom(this.fetchEntityData().pipe(take(1)));
    const customForm = await this._getCustomFormSvc.getContactForm(data.getEntity.entityType.id, this.campaignId);

    if (!customForm) {
      this._toastrService.error('No contact form found for this entity type in the campaign');
      return;
    }


    this.entity = data.getEntity;
    this.customForm = cloneDeep(customForm);
    this.customFormComponents = this.buildComponentsArray(this.customForm);
    this.loading = false;

    this.watchEntityData();
    //this.fetchEntityData
    this._cd.detectChanges();
  }

  private buildComponentsArray(customForm: CustomFormDetailsFragment) {
    // replace demographics object attribute with language and dob items
    // NOTE: This exists due to backend database changes. We use to have all of
    // these items in a single input. Then we split preferred lanugage and date of birth
    // and then a few months later we removed preferred_language to replace with entityLanguages
    // array. To avoid breaking existing forms we do this transformation here.
    customForm.fields = customForm.fields.map(field => {
      if (field.objectAttribute === 'demographics' && field.fieldObjectType === CustomFormFieldObjectTypeTypes.Entity) {
        return [
          {
            ...field,
            objectAttribute: 'preferred_language'
          },
          {
            ...field,
            objectAttribute: 'date_of_birth'
          }
        ];
      }
      return [field];
    }).flat();

    return customForm.fields.map(field => ({
      component: this.getComponent(field),
      formField: field
    }));
  }

  private getComponent(field: CustomFormFieldDetailsFragment): string {
    if (field.fieldObjectType === CustomFormFieldObjectTypeTypes.Address) {
      return 'app-entity-addresses-table';
    }
    if (field.fieldObjectType === CustomFormFieldObjectTypeTypes.PhoneNumber) {
      return 'app-entity-phones-table';
    }
    if (field.fieldObjectType === CustomFormFieldObjectTypeTypes.Email) {
      return 'app-entity-emails-table';
    }
    if (field.fieldObjectType === CustomFormFieldObjectTypeTypes.GlobalNote) {
      return 'app-create-and-view-notes';
    }
    if (field.fieldObjectType === CustomFormFieldObjectTypeTypes.TagCategory) {
      return 'app-applied-tag-category-table';
    }
    if (field.fieldObjectType === CustomFormFieldObjectTypeTypes.EntityConnectionType) {
      return 'app-entity-connection-table';
    }
    if (field.fieldObjectType === CustomFormFieldObjectTypeTypes.Entity) {
      if (field.objectAttribute === 'social_profile') {
        return 'app-entity-social-table';
      }
      if (field.objectAttribute === 'name') {
        return 'app-entity-name-table';
      }
      if (field.objectAttribute === 'preferred_language') {
        return 'app-entity-language-table';
      }
      if (field.objectAttribute === 'date_of_birth') {
        return 'app-entity-dob-table';
      }

      if (field.objectAttribute === 'assessment') {
        return 'app-entity-assessment-add';
      }
    }

    console.warn('No component found for field', field);
    return null;
  }

  onViewAllNotesClicked() {
    NoteDialogComponent.open(this._dialog, {
      data: {
        campaignId: this.campaignId,
        targetType: NoteTarget.Entity,
        targetId: this.entityId,
        noteType: 'text',
        heading: 'View Notes',
        hideNotes: false,
        keepOpenAfterSave: false
      }
    });
  }

  triggerRefetch() {
    // noop
  }

  private watchEntityData() {
    this.fetchEntityData().pipe(
      skip(1)
    ).subscribe(({ data }) => {
      this.entity = data.getEntity;
      this._cd.detectChanges();
    });
  }

  private fetchEntityData() {
    return this._getEntityByIdGQL.watch({
      entityId: this.entityId,
      campaignId: this.campaignId,
      withAddresses: true,
      withEmails: true,
      withPhones: true,
      withTags: true,
      withFollowUpDetails: true,
      withToplines: true,
      withHighligtedTopline: true,
      withBestAddress: true,
      withBestEmail: true,
      withBestPhone: true,
      withEntityType: true
    }).valueChanges
      .pipe(
        takeUntil(this.disposer)
      );
  }
}
